import { createFileRoute, Link } from '@tanstack/react-router'
import { useSelector, useDispatch } from 'react-redux'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useState } from 'react'
import { toast } from 'sonner'
import { addFeedback, updateFeedback } from '@/store/actions'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { selectJobOverviewData, selectCheckinByBooking } from '@/store/selectors'
import { RootState } from '@/store/store'

export const Route = createFileRoute('/job/$jobId/$date/signoff/')({
  component: JobSignOffIndexPage,
})

function JobSignOffIndexPage() {
  const { jobId, date } = Route.useParams()
  const navigate = Route.useNavigate()
  const dispatch = useDispatch()

  const [reason, setReason] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const { job, booking } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  )

  const checkin = useSelector((state: RootState) =>
    booking ? selectCheckinByBooking(state, booking.id, booking.date) : null
  )

  const feedbackToday = useSelector((state: RootState) =>
    state.feedback.items.find(feedback =>
      booking &&
      feedback.booking_id === booking.id &&
      feedback.booking_date === booking.date
    )
  )

  const handleProceed = () => {
    navigate({ to: '/job/$jobId/$date/signoff/questions', params: { jobId, date } })
  }

  const handleUnavailable = () => {
    setIsDialogOpen(true)
  }

  const submitFeedback = () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason')
      return
    }

    if (feedbackToday) {
      dispatch(updateFeedback({
        ...feedbackToday,
        feedback: 'ho_unavailable',
        reason: reason.trim(),
      }))
    } else if (job && booking && checkin) {
      dispatch(addFeedback({
        feedback: 'ho_unavailable',
        reason: reason.trim(),
        job_id: job.id,
        checkin_id: checkin.id,
        booking_id: booking.id,
        booking_date: booking.date,
        company_id: job.company_id,
      }))
    }

    setIsDialogOpen(false)
    navigate({ to: '/' })
  }

  if (!job || !booking) {
    return <div>Job not found</div>
  }

  return (
    <>
      {/* Content */}
    <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
      <div className="p-8 flex items-center ">
        <div className="max-w-4xl w-full space-y-8 mx-auto flex flex-col items-center">
            <div className="">
              <div className=" mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Customer Signature required.
                </h2>
                <h3 className="text-2xl text-gray-700">
                  Please pass the device to the customer
                </h3>
              </div>

              <div className="flex gap-4">
                <Button onClick={handleProceed}>
                  I am the homeowner
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleUnavailable}
                >
                  Homeowner is unavailable
                </Button>
              </div>
            </div>
        </div>
      </div>
          </ScrollArea>

      {/* Unavailable Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Homeowner Unavailable</DialogTitle>
            <DialogDescription>
              Please give detail about why the homeowner is unavailable.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col justify-between gap-2 rounded-lg mb-4">
            <p className="text-red-800 leading-tight font-bold">
              Before continuing, please contact the Office for approval!
            </p>
            <p className="text-red-700 text-sm">
              Failure to obtain prior permission will result in timesheets
              being rejected and payments declined
            </p>
            <a
              href="tel:01376519743"
              className="text-blue-800 font-black text-xl hover:underline"
            >
              01376 519743
            </a>
          </div>

          <Textarea
            placeholder="Please give detail..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mb-4"
            rows={4}
          />

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitFeedback}>
              Submit Feedback
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
